/**
 * GlobalVoiceProvider
 *
 * App-wide voice context provider. Used to be the mount point for the
 * always-visible FloatingLuanaAvatar (removed 2026-05-26) and the
 * LuanaChatPopup (deleted 2026-05-26 in T1/PR #47). After the
 * consolidation the only persistent UI it still hosts is the
 * PhoneticAlphabetPopup (spell helper).
 *
 * The FloatingLuanaAvatar COMPONENT still exists — ApprovalModal and
 * JobIntelligencePanel use it for their own feature-scoped voice
 * affordances (voice approval, voice identify). What got removed was
 * the GLOBAL bottom-right always-visible mount.
 *
 * Consequences of the global-mount removal:
 *   - No "Hey Lu" hands-free wake word from anywhere on the page.
 *     Voice input now happens via the explicit mic button in
 *     LuanaChat (added in PR #52).
 *   - No click-to-focus-sidebar shortcut from the floating avatar.
 *     User clicks the sidebar textarea directly.
 *
 * VoiceContext stays in place even though no current consumer uses
 * isListening — it's cheap to keep and may host future voice state
 * shared across surfaces.
 *
 * History:
 *   - 2026-05-26 (T1): killed LuanaChatPopup.
 *   - 2026-05-26 (T4): added avatar → focus-sidebar event dispatch.
 *   - 2026-05-26 (T3): killed /command-center, removed isCC branch.
 *   - 2026-05-26 (post-T3): removed the global FloatingLuanaAvatar
 *     mount. Component kept for ApprovalModal + JobIntelligencePanel
 *     use; only the always-visible bottom-right mount is gone.
 */

import { useState, useEffect } from 'react';
import PhoneticAlphabetPopup from './PhoneticAlphabetPopup';
import { VoiceContext } from '../contexts/VoiceContext';

interface Props {
  children: React.ReactNode;
}

export default function GlobalVoiceProvider({ children }: Props) {
  const [isListening, setIsListening] = useState(false);
  const [spellOpen,   setSpellOpen]   = useState(false);

  // Sidebar (and anyone else) opens/toggles the floating spell helper
  // by dispatching a window event. Decoupled from the sidebar via
  // event so we don't have to thread state through the layout.
  useEffect(() => {
    const onOpen   = () => setSpellOpen(true);
    const onToggle = () => setSpellOpen(p => !p);
    window.addEventListener('spell-helper:open',   onOpen);
    window.addEventListener('spell-helper:toggle', onToggle);
    return () => {
      window.removeEventListener('spell-helper:open',   onOpen);
      window.removeEventListener('spell-helper:toggle', onToggle);
    };
  }, []);

  return (
    <VoiceContext.Provider value={{ isListening, setIsListening }}>
      {children}
      <PhoneticAlphabetPopup isOpen={spellOpen} onClose={() => setSpellOpen(false)} />
    </VoiceContext.Provider>
  );
}
